import { DestroyRef, inject, Injectable, NgZone } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Store } from '@ngxs/store'
import {
  Observable,
  fromEvent,
  switchMap,
  timer,
  of,
  BehaviorSubject,
  merge,
  filter,
  tap,
  take,
  combineLatest,
  catchError,
  EMPTY,
  distinctUntilChanged,
  debounceTime,
  map,
  finalize
} from 'rxjs'
import moment from 'moment'
import { PreferenceState } from '../../../store/preference/preference.state'
import { ConfigService } from '../config.service'
import { FeatureParams } from '../../model/config'
import { ZoomVideoState } from '../../../store/zoom-video/zoom-video.state'
import { UserState } from '../../../store/user/user.state'
import { PccState } from '../../../store/pcc/pcc.state'
import { AuthState } from '../../../store/auth/auth.state'
import { NetworkService } from '../network.service'
import { DepartmentState } from '../../../store/department/department.state'
import { ReportState } from '../../../store/report/report.state'
import { OfflineReportingState } from '../../../store/offline-reporting/offline-reporting.state'
import { OfflineActivityService } from '../offline-activity.service'

@Injectable({
  providedIn: 'root'
})
export class UserActivityService {
  private readonly ngZone = inject(NgZone)
  private readonly store = inject(Store)
  private readonly configService = inject(ConfigService)
  private readonly offlineActivityService = inject(OfflineActivityService)
  private readonly preferenceState = inject(PreferenceState)
  private readonly pccState = inject(PccState)
  private readonly authState = inject(AuthState)
  private readonly departmentState = inject(DepartmentState)
  private readonly reportState = inject(ReportState)
  private readonly networkService = inject(NetworkService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly offlineReportingState = inject(OfflineReportingState)

  private readonly sourceClick$: Observable<Event> = fromEvent(window, 'click')
  private readonly sourceTouchmove$: Observable<Event> = fromEvent(
    window,
    'mousemove'
  )
  private readonly sourceTouchStart$: Observable<Event> = fromEvent(
    window,
    'touchstart'
  )
  private readonly isUserCNA$ = this.store.select(UserState.isUserCNA)
  private readonly isUserRN$ = this.store.select(UserState.isUserRN)
  private readonly isPccUser$ = this.store.select(UserState.isPccUser)

  private readonly isMobile = this.preferenceState.isMobile
  private readonly deviceUpdateSubject = new BehaviorSubject<boolean>(false)
  private readonly deviceUpdate$ = this.deviceUpdateSubject.asObservable()
  private readonly screenSaverDebounceTime = this.configService.getNumberValue(
    FeatureParams.FeatureScreenSaverDebounceTime,
    5
  )
  private readonly sessionExpirationDuration =
    this.configService.getNumberValue(
      FeatureParams.FeatureSessionExpirationDuration,
      45
    )
  protected readonly inSession = this.store.selectSignal(
    ZoomVideoState.canVideoBeRendered
  )
  protected readonly isSessionExpired = this.store.selectSignal(
    PreferenceState.sessionExpire
  )
  private readonly screenSaver$ = this.store.select(PreferenceState.screenSaver)

  protected readonly isOffline$ = this.networkService.isOfflineDebounced$
  protected readonly isOnline$ = this.networkService.isOnlineObs$

  private wakeLock: WakeLockSentinel | null = null

  constructor() {
    this.keepUserScreenWakeUntilScreenSaverAppears()
  }

  public subscribeToSessionActivityTracking(): void {
    this.ngZone.runOutsideAngular(() => {
      this.subscribeOnWindowsClickAndTouchmove().subscribe()
      if (this.isMobile) {
        return this.detectRnSessionExpiration().subscribe()
      } else {
        return this.detectSessionExpiration().subscribe()
      }
    })
  }

  public setDeviceUpdateSubject(value: boolean): void {
    this.deviceUpdateSubject.next(value)
  }

  private subscribeOnWindowsClickAndTouchmove() {
    return merge(
      this.sourceClick$,
      this.sourceTouchmove$,
      this.sourceTouchStart$,
      this.deviceUpdate$.pipe(filter(value => value))
    ).pipe(
      switchMap(state => {
        const timerTime = moment().add(this.screenSaverDebounceTime, 'minutes')
        if (
          !this.store.selectSnapshot(PreferenceState.screenSaver) &&
          !this.offlineActivityService.isPopupAboutWaitingWiFiToBeResumedShown()
        ) {
          this.preferenceState.setLastActivityTime()
        }
        return state ? timer(timerTime.toDate()) : of(null)
      }),
      tap(() => {
        if (!this.inSession() && this.authState.isAuthenticated()) {
          this.preferenceState.setScreenSaver(true)
          if (this.preferenceState.getState().isSimulationMode) {
            this.preferenceState.setIsSimulationMode(false)
          }
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  }

  private detectRnSessionExpiration() {
    return merge(
      this.sourceClick$,
      this.sourceTouchmove$,
      this.sourceTouchStart$
    ).pipe(
      switchMap(state => {
        const timerTime = moment().add(
          this.sessionExpirationDuration,
          'minutes'
        )
        return state ? timer(timerTime.toDate()) : of(null)
      }),
      filter(() => !this.inSession()),
      switchMap(() =>
        combineLatest([this.isUserRN$, this.isUserCNA$, this.isPccUser$]).pipe(
          take(1)
        )
      ),
      switchMap(([isUserRN, isUserCNA, isPccUser]) => {
        if (isUserCNA && isPccUser) {
          this.preferenceState.setSessionExpire(true)
          this.handlerClickLogout()
          return of(null)
        }
        if (isUserRN) {
          return this.pccState.getPccIsAuthStatus(true).pipe(
            catchError(() => {
              this.preferenceState.setSessionExpire(true)
              this.handlerClickLogout()
              return EMPTY
            })
          )
        }
        return of(null)
      }),
      switchMap(token =>
        this.isOnline$.pipe(
          filter(isOnline => isOnline),
          debounceTime(4000),
          distinctUntilChanged(),
          take(1),
          map(() => token)
        )
      ),
      switchMap(token => {
        if (token) {
          return of(token)
        }
        if (this.authState.getState().CNAAccessJwt) {
          this.handlerClickRnLogout()
          this.setSessionExpired(true)
        }
        return of(null)
      }),
      tap(pccTokenStatus => {
        if (pccTokenStatus !== null && !this.isSessionExpired()) {
          this.preferenceState.setSessionExpire(true)
          this.handlerClickLogout()
        }
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  }

  private detectSessionExpiration() {
    return merge(
      this.sourceClick$,
      this.sourceTouchmove$,
      this.sourceTouchStart$
    ).pipe(
      switchMap(state => {
        const timerTime = moment().add(
          this.sessionExpirationDuration,
          'minutes'
        )
        return state ? timer(timerTime.toDate()) : of(null)
      }),
      filter(() => !this.inSession()),
      tap(() => {
        if (!this.authState.isAuthenticated()) {
          return
        }
        this.preferenceState.setSessionExpire(true)
        this.handlerClickLogout()
      }),
      takeUntilDestroyed(this.destroyRef)
    )
  }

  private handlerClickRnLogout(): void {
    this.authState.rnLogout()
    this.preferenceState.setPreferenceIsCollapseMenu(true)
    this.reportState.setSuccessfullySentCurrentShiftPatientAllMeasurements(null)
    this.reportState.setIsPatientVitalsTakenPopupVisible(false)
    this.offlineReportingState.resetState()
  }

  private handlerClickLogout(): void {
    this.offlineReportingState.resetWithoutEntitiesStateButRemovedDataToBeReported()
    this.systemLogOut()
  }

  private keepUserScreenWakeUntilScreenSaverAppears(): void {
    if (this.isMobile) {
      return
    }
    this.screenSaver$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(active => {
        if (active) {
          if (this.wakeLock) {
            this.wakeLock.release()
            this.wakeLock = null
          }
        } else {
          try {
            navigator.wakeLock.request('screen').then(wakeLock => {
              this.wakeLock = wakeLock
            })
          } catch (error) {
            console.error('Error requesting screen lock api', error)
          }
        }
      })
  }

  private setSessionExpired(isExpired: boolean): void {
    this.preferenceState.setSessionExpire(isExpired)
  }

  private systemLogOut(): void {
    this.authState.logout()
  }
}
